import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, Plus, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtWhen } from "./ui";

const DEAL = /(featured|happy ?hour|mall|special|code|deal|promo)/i;
const money = (o) => `${o.from_price ? "from " : ""}$${o.price}`;

const NewServices = ({ items, onAdded }) => {
  const [pick, setPick] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!items.length) return null;
  const toggle = (u) => setPick(pick.includes(u) ? pick.filter((x) => x !== u) : [...pick, u]);
  const add = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/admin/services/fresha-import", { urls: pick });
      toast.success(`${data.added.length} service${data.added.length === 1 ? "" : "s"} added to the website`);
      setPick([]); onAdded();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="fs-block" data-testid="fresha-new-services">
      <h4>Add from Fresha ({items.length} not on the website)</h4>
      <p className="muted-sm">Tick the ones you want. Deals go into “Specials”; the rest go into the matching category, with Fresha's price, length, description and link.</p>
      {items.map((o, i) => (
        <label key={o.url} className="fs-row" data-testid={`fresha-new-${i}`}>
          <input type="checkbox" checked={pick.includes(o.url)} onChange={() => toggle(o.url)} data-testid={`fresha-new-check-${i}`} />
          <span><strong>{o.name}</strong><small>{o.category}{DEAL.test(o.category) ? " → Specials" : ""} · {o.duration || "—"}</small></span>
          <b>{o.price != null ? money(o) : "No price"}</b>
        </label>
      ))}
      <div className="req-actions">
        <button className="btn light" type="button" onClick={() => setPick(pick.length === items.length ? [] : items.filter((o) => o.price != null).map((o) => o.url))} data-testid="fresha-new-select-all">{pick.length === items.length ? "Clear" : "Select all"}</button>
        <button className="btn gold" type="button" disabled={busy || !pick.length} onClick={add} data-testid="fresha-import-button">{busy ? <Loader2 size={15} className="spin" /> : <><Plus size={14} /> Add {pick.length || ""} selected</>}</button>
      </div>
    </div>
  );
};

const Log = ({ log }) => (
  <details className="fs-block" data-testid="fresha-sync-log">
    <summary>Fresha sync log ({log.length})</summary>
    {log.map((e) => (
      <div key={e.id} className="fs-log" data-testid={`fresha-log-${e.id}`}>
        <small>{fmtWhen(e.at)} · {e.source === "nightly" ? "Nightly" : "Refresh now"}</small>
        {e.error ? <p className="form-error">{e.error}</p> : (
          <p>{e.price_changes.length ? e.price_changes.map((c) => `${c.service}: ${c.old} → ${c.new}`).join(" · ") : "No price changes"}
            {e.linked.length > 0 && ` · ${e.linked.length} newly linked`}{e.missing.length > 0 && ` · ${e.missing.length} not found on Fresha`}</p>
        )}
      </div>
    ))}
  </details>
);

export const FreshaSync = ({ onSynced }) => {
  const [busy, setBusy] = useState("");
  const qc = useQueryClient();
  const { data: log } = useQuery({ queryKey: ["admin", "fresha-log"], queryFn: () => api.get("/admin/services/fresha-log").then((r) => r.data) });
  const last = log?.find((e) => !e.error);
  const reload = async () => {
    await Promise.all([qc.refetchQueries({ queryKey: ["admin", "services"] }), qc.refetchQueries({ queryKey: ["admin", "fresha-log"] })]);
    qc.invalidateQueries({ queryKey: ["services"] });
    onSynced();
  };
  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "refresh") {
        const { data } = await api.post("/admin/services/fresha-refresh");
        toast.success(`Fresha refreshed · ${data.price_changes.length} price change${data.price_changes.length === 1 ? "" : "s"}`);
      } else {
        await api.post("/admin/services/fresha-sync", { overwrite: true });
        await api.post("/admin/services/fresha-refresh");
        toast.success("All services re-matched to Fresha");
      }
      await reload();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };
  return (
    <div className="ad-card pp-form" data-testid="fresha-sync">
      <h3>Fresha sync</h3>
      <p className="muted-sm">Every night at 3am the website copies prices and links from your Fresha page, so Fresha stays the source of truth. New Fresha services and ones removed from Fresha are listed here — nothing is added or deleted on its own.</p>
      <div className="req-actions">
        <button className="btn gold" disabled={!!busy} onClick={() => run("refresh")} data-testid="fresha-refresh-button">{busy === "refresh" ? <Loader2 size={15} className="spin" /> : <><RefreshCw size={14} /> Refresh now</>}</button>
        <button className="btn light" disabled={!!busy} onClick={() => window.confirm("Re-match every service, replacing links already set?") && run("rematch")} data-testid="fresha-resync-all-button">{busy === "rematch" ? <Loader2 size={15} className="spin" /> : "Re-match all"}</button>
      </div>
      {last && (
        <p className="muted-sm" data-testid="fresha-sync-report">Last checked {fmtWhen(last.at)} · <strong>{last.price_changes.length}</strong> price change{last.price_changes.length === 1 ? "" : "s"} · <strong>{last.new.length}</strong> Fresha services not on the website · <strong>{last.missing.length}</strong> website services not found on Fresha</p>
      )}
      {last?.missing.length > 0 && <p className="muted-sm" data-testid="fresha-sync-missing">Not found on Fresha any more (check or remove): {last.missing.join(", ")}</p>}
      {last && <NewServices items={last.new} onAdded={() => run("refresh")} />}
      {log?.length > 0 && <Log log={log} />}
    </div>
  );
};
